import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Layout from "@wrappers/layouts/main-layout/Layout";
import { updateActiveModule } from "@features/activeModuleSlice";
import { updateTitle } from "@features/titleSlice";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { getAccessToken } from "@ts/retrieve-data";
import {
    AppRoutes,
    ParentModule,
    Platforms,
    SubModule,
    TActiveClientId,
    TReportsData,
    TSelectedReportData,
} from "@ts/common-types";
import AgGridLoaderComponent from "@components/loaders/AgGridLoaderComponent";
import { setSelectedReportData } from "@/features/gridDataSlice";
import { getActiveModule } from "@/ts/utils";
import { useNavigate } from "react-router-dom";

const ReportsComponent = () => {
    const reportsData = useSelector(
        (state: TReportsData) => state.reportsData.value
    );
    const reportsDataLoading = useSelector(
        (state: TReportsData) => state.reportsData.isLoading
    );
    const selectedReportData = useSelector(
        (state: TSelectedReportData) => state.selectedReportData.value
    );
    const [accessToken, setAccessToken] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const { activeModule: moduleName, reportIdFromUrl } = getActiveModule();
    const moduleState = [ParentModule.REVSTACK, SubModule.OVERVIEW].includes(
        moduleName
    )
        ? "revstack"
        : "common";
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const shouldNavigateHome = useMemo(() => {
        return (
            moduleState === ParentModule.REVSTACK &&
            !reportsDataLoading &&
            !reportsData[moduleState]?.length
        );
    }, [moduleState, reportsDataLoading, reportsData, ParentModule]);

    useEffect(() => {
        getAccessToken()
            .then((response) => {
                if (response.status) {
                    setAccessToken(response.accessToken);
                }
            })
            .catch((err) => {
                console.error(err);
            });

        if (shouldNavigateHome) {
            navigate("/");
        }

        if (reportsData[moduleState]?.length) {
            if (!reportIdFromUrl) {
                const firstReport = reportsData.revstack[0];
                navigate(
                    `/${Platforms.PLANNING}/${ParentModule.REVSTACK}/${firstReport.report_id}`
                );
            } else {
                const selectedReport = reportsData[moduleState].find(
                    (report) => report.report_id === reportIdFromUrl
                );
                if (!selectedReport && !reportsData[moduleState].length) {
                    navigate(`/${AppRoutes.PAGENOTFOUND}`);
                } else {
                    dispatch(
                        setSelectedReportData(
                            selectedReport || reportsData[moduleState][0]
                        )
                    );
                    dispatch(
                        updateTitle(
                            selectedReport?.report_name ||
                            reportsData[moduleState][0].report_name
                        )
                    );
                    setIsLoading(false);
                }
            }
        } else {
            setIsLoading(false);
        }
        dispatch(updateActiveModule(moduleName));
    }, [activeClientId, reportsData, dispatch, moduleName]);

    useEffect(() => {
        if (reportsData[moduleState].length && reportIdFromUrl) {
            const selectedReport = reportsData[moduleState].find(
                (report) => report.report_id === reportIdFromUrl
            );
            dispatch(
                updateTitle(
                    reportIdFromUrl
                        ? selectedReport?.report_name
                        : reportsData[moduleState][0].report_name
                )
            );
        }
    }, [reportIdFromUrl, dispatch, moduleState, reportsData]);

    return (
        <>
            <Layout>
                {!isLoading ? (
                    reportsData[moduleState].length ? (
                        selectedReportData && (
                            <div className="m-0">
                                <div className="powerbi-embed-container">
                                    <PowerBIEmbed
                                        embedConfig={{
                                            type: "report",
                                            id: selectedReportData?.report_id,
                                            embedUrl:
                                                selectedReportData?.embed_url,
                                            accessToken,
                                            tokenType: models.TokenType.Aad,
                                            settings: {
                                                panes: {
                                                    filters: {
                                                        expanded: false,
                                                        visible: true,
                                                    },
                                                },
                                            },
                                        }}
                                        eventHandlers={
                                            new Map([
                                                [
                                                    "loaded",
                                                    function () {
                                                        // console.log("Report loaded");
                                                    },
                                                ],
                                                [
                                                    "rendered",
                                                    function () {
                                                        // console.log("Report rendered");
                                                    },
                                                ],
                                                [
                                                    "error",
                                                    function () {
                                                        // console.log(event.detail);
                                                    },
                                                ],
                                            ])
                                        }
                                        cssClassName={"reportContainer"}
                                        getEmbeddedComponent={() => {
                                            // window.report = embeddedReport;
                                        }}
                                    />
                                </div>
                            </div>
                        )
                    ) : (
                        <div className="no-reports-available">
                            <span>No reports available</span>
                        </div>
                    )
                ) : (
                    <AgGridLoaderComponent />
                )}
            </Layout>
        </>
    );
};

export default ReportsComponent;
